/**
 * Where a read-only firm page was opened from (CP-04). National IT and the Board open a firm from the National view
 * or from Leadership (with its tab and picked firm in the address); the firm pages' "Back to ..." link returns there.
 * Links pass the origin as router state; the first firm page keeps it for the session, so moving between that
 * firm's pages keeps the way back. Without one, the tier decides (National IT: the National view; the Board: Leadership).
 */
import { useLocation } from 'react-router-dom';

const KEY = 'grc.networkOrigin';

/** The router state a link into a firm's pages passes: the page it was opened from, with its query string. */
export function useFrom(): { from: string } {
  const l = useLocation();
  return { from: `${l.pathname}${l.search}` };
}

const isNetworkPage = (p: unknown): p is string => typeof p === 'string' && (p === '/national' || p.startsWith('/national?') || p === '/leadership' || p.startsWith('/leadership?'));

/** The way back for a read-only firm page, or null while the caller's tier is not known yet. */
export function useBackTo(firmCode: string, tier: { national: boolean } | null): { to: string; label: string } | null {
  const state = useLocation().state as { from?: unknown } | null;
  let from: string | null = isNetworkPage(state?.from) ? state.from : null;
  // Keep the origin for this firm's other pages; storage may be unavailable (private window), then the tier decides.
  try {
    if (from) sessionStorage.setItem(KEY, JSON.stringify({ firmCode, from }));
    else {
      const kept = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as { firmCode?: string; from?: unknown } | null;
      if (kept?.firmCode === firmCode && isNetworkPage(kept.from)) from = kept.from;
    }
  } catch {
    /* the tier rule below still gives a way back */
  }
  if (from) return { to: from, label: from.startsWith('/national') ? 'Back to the National view' : 'Back to Leadership' };
  if (!tier) return null;
  return tier.national ? { to: '/national', label: 'Back to the National view' } : { to: '/leadership?view=network', label: 'Back to Leadership' };
}
